import {
  hasRequiredCategorizedUploads,
  hasUntaggedLegacyUpload,
} from 'tio-common/utils/tuition-assistance/categorized-upload';
import type {
  CategorizedUploadAssetLike,
  DocumentEntryLike,
} from 'tio-common/utils/tuition-assistance/categorized-upload';

/**
 * The shared "are the required documents actually there?" submit gate for the
 * two employee-facing evidence surfaces: the legacy application show page's
 * Attachments section and the manage screen's Submit-Evidence task card.
 *
 * It exists because the two disagreed. The show page was fixed in #9908 to read
 * the CONFIGURED document rows (`hasRequiredCategorizedUploads`), while the
 * manage screen still delegated to the model's `hasRequiredEvidenceAttachments`
 * — a bare presence check that cannot see the rows. So one evidence file
 * satisfied it and Submit Evidence went live at "1 / 3 required", with two
 * empty required rows still on screen (#10426).
 *
 * Pure functions over plain inputs so the gate is unit-testable without
 * rendering, and so a future third surface adopts the same rule by calling it
 * rather than by re-deriving it — the same reasoning as `deriveAttention` and
 * `deriveDisbursement`.
 */

export interface CategorizedUploadConfig {
  /**
   * The company's "AI Attachment Summaries & Real-Time Validation" setting.
   * Categorized upload is a company capability, not a per-browser beta opt-in,
   * and the setting defaults ON — hence the `=== false` test rather than a
   * truthiness check (#9889; matches the admin surfaces).
   */
  isAiSummariesEnabled: boolean | undefined;
  /** The template's configured rows for THIS classification. */
  documentEntries: readonly DocumentEntryLike[];
}

/**
 * Whether the categorized (row-per-document-type) uploader is what renders,
 * as opposed to the legacy free-form one. The submit gate and the rendered
 * component must answer this identically — if the gate assumed rows that were
 * never drawn, the employee would face a permanently disabled Submit.
 */
export function usesCategorizedUpload({
  isAiSummariesEnabled,
  documentEntries,
}: CategorizedUploadConfig): boolean {
  if (isAiSummariesEnabled === false) {
    return false;
  }
  return documentEntries.length > 0;
}

export interface RequiredDocumentsInput extends CategorizedUploadConfig {
  /**
   * Whether documents are required at all for this step — the evidence surface
   * passes `application.isEvidenceRequired`, which already honors the
   * per-application waiver. Rows alone are NOT a requirement: a template that
   * lists rows with the requirement off stays submittable.
   */
  isRequired: boolean;
  /** `COMPLETION_DOCUMENTS` on the evidence surface; the show page is phase-aware. */
  classification: string;
  /** The assets the uploader itself renders from (application-level). */
  assets: readonly CategorizedUploadAssetLike[];
  /**
   * Presence fallback for the free-form branch — the model's own
   * `hasRequiredEvidenceAttachments`, which checks the application level or
   * every active course. Only consulted when no categorized rows are configured.
   */
  hasLegacyRequiredAttachments: boolean;
}

/**
 * True when this step's document requirement is satisfied.
 *
 * Presence only — AI validation verdicts are deliberately never consulted, as a
 * verdict can be orphaned at `pending` when no browser session sees the AI
 * response (tio-api#7874), which hard-blocked employees when the gate keyed off
 * them (#9760).
 */
export function hasRequiredDocuments(input: RequiredDocumentsInput): boolean {
  if (!input.isRequired) {
    return true;
  }

  if (usesCategorizedUpload(input)) {
    if (hasRequiredCategorizedUploads(input.assets, input.classification, input.documentEntries)) {
      return true;
    }
    // Carryover escape hatch, preserved from the #9908 gate: evidence uploaded
    // BEFORE this template grew document rows carries no row key and no declared
    // type, so it can satisfy no configured entry. An application can sit in
    // ATTEND for months, so a template that grows rows mid-flight must not
    // hard-block the employee — the #9760 failure mode.
    // Note this is deliberately looser than the rows: ONE such file unlocks
    // submission with every row still empty. That was airtight when nothing on
    // screen could re-file the file; CategorizedUpload's Other-documents tray now
    // offers a "File under…" select (other-documents-list.gts), so the hatch is
    // wider than it strictly needs to be. Narrowing it changes behavior on the
    // show page and the guided wizard too, so it is left as-is here and tracked
    // separately rather than folded into this fix.
    return hasUntaggedLegacyUpload(input.assets, input.classification);
  }

  return input.hasLegacyRequiredAttachments;
}
